/**
 * WhatsApp Chat panel — presentational UI.
 * Receives messages, status, sendMessage from useChatConnection hook.
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaComments, FaPaperPlane, FaSpinner, FaExclamationTriangle, FaEnvelope, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import InviteTemplateModal from "./InviteTemplateModal";

function Chat({
  chatConnection = {},
  studyId = "",
  language = "en",
  studyData = null,
  embedded = false,
  hideNumberEntry = false,
  hideInviteButton = false,
  defaultParam3 = "",
} = {}) {
  // Extract values from chatConnection with defaults
  // Memoize values used in dependency arrays to prevent unnecessary re-renders
  const messages = useMemo(() => chatConnection.messages || [], [chatConnection.messages]);
  const status = useMemo(() => chatConnection.status || "idle", [chatConnection.status]);
  const errorMessage = useMemo(() => chatConnection.errorMessage || "", [chatConnection.errorMessage]);
  const hasPatientResponded = useMemo(() => chatConnection.hasPatientResponded || false, [chatConnection.hasPatientResponded]);
  const hasTemplateSent = useMemo(() => chatConnection.hasTemplateSent || false, [chatConnection.hasTemplateSent]);
  const canSendFreeFormMessage = useMemo(() => chatConnection.canSendFreeFormMessage || false, [chatConnection.canSendFreeFormMessage]);
  
  // Functions and other values (functions from hooks should be stable)
  const sendMessage = chatConnection.sendMessage;
  const hasConversation = chatConnection.hasConversation || false;
  const connectedNumber = chatConnection.connectedNumber || "";
  const changeNumber = chatConnection.changeNumber;
  const deleteConversation = chatConnection.deleteConversation;
  const connectWithNumber = chatConnection.connectWithNumber;
  const sendInvite = chatConnection.sendInvite;
  const { t } = useTranslation("common");
  const isRTL = language === "he" || language === "ar";
  const [inputText, setInputText] = useState("");
  const [manualNumber, setManualNumber] = useState("");
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [numberError, setNumberError] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [is24HourBannerDismissed, setIs24HourBannerDismissed] = useState(false);
  const messagesContainerRef = useRef(null);

  const isValidPhoneNumber = (phone) => {
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, "");
    // WhatsApp numbers should be 7-15 digits (E.164 format without +)
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  };

  const scrollToBottom = useCallback(() => {
    const el = messagesContainerRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Auto-fill referring physician number when field is empty
  useEffect(() => {
    const referringPhysicianNumber = studyData?.insuranceNumber || "";
    if (referringPhysicianNumber && !manualNumber && (status === "awaitingNumber" || status === "idle")) {
      setManualNumber(referringPhysicianNumber);
    }
  }, [studyData, status, manualNumber]);

  // Reset connecting state and clear local number when status changes
  useEffect(() => {
    if (status === "ready" || status === "error" || status === "awaitingNumber") {
      setIsConnecting(false);
    }
    // Clear local manualNumber when status changes to awaitingNumber (after delete/change)
    // Auto-fill will happen in the effect above if studyData?.insuranceNumber exists
    if (status === "awaitingNumber") {
      // Only clear if no referringPhysicianNumber to auto-fill
      if (!studyData?.insuranceNumber) {
        setManualNumber("");
      }
      setNumberError("");
      setIs24HourBannerDismissed(false); // Reset banner dismissal on new conversation
    }
  }, [status, studyData]);

  // Reset banner dismissal when patient responds (show banner again)
  useEffect(() => {
    if (hasPatientResponded) {
      setIs24HourBannerDismissed(false);
    }
  }, [hasPatientResponded]);

  const handleSend = async () => {
    const text = (inputText || "").trim();
    if (!text) return;
    
    // Prevent sending if template not sent or waiting for response
    if (!canSendFreeFormMessage) {
      if (!hasTemplateSent) {
        toast.warning(t("chatSendTemplateFirst") || "Please send a template message first to start the conversation.");
        return;
      }
      if (!hasPatientResponded) {
        toast.info(t("chatWaitingForResponse") || "Waiting for patient response. You can send messages after they reply.");
        return;
      }
    }
    
    setInputText("");
    try {
      await sendMessage(text);
    } catch (err) {
      console.error("Send error:", err);
      if (err.requiresTemplate || err.message?.includes("24-hour")) {
        toast.error(t("chat24HourWindowExpired") || "24-hour window expired. Please send a template message first.");
      } else {
        toast.error(t("chatSendFailed"));
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleChangeNumber = () => {
    if (changeNumber) {
      setManualNumber(""); // Clear local state
      setNumberError(""); // Clear any errors
      changeNumber();
      toast.info(t("chatNumberChanged"));
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConversation) return;
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    try {
      await deleteConversation();
      setManualNumber(""); // Clear local state
      setNumberError(""); // Clear any errors
      toast.success(t("chatDeleted") || "Conversation deleted");
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(t("chatDeleteFailed") || "Failed to delete conversation");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConnect = () => {
    const number = manualNumber.trim();
    console.log("[Chat] handleConnect called with number:", number);
    if (!number) {
      setNumberError(t("chatEnterNumber") || "Please enter a WhatsApp number");
      return;
    }
    if (!isValidPhoneNumber(number)) {
      setNumberError(t("chatInvalidPhoneNumber") || "Invalid phone number. Must be 7-15 digits.");
      return;
    }
    setNumberError("");
    setIsConnecting(true);
    console.log("[Chat] Calling connectWithNumber:", connectWithNumber);
    if (connectWithNumber) {
      connectWithNumber(number);
      console.log("[Chat] connectWithNumber called");
      // Note: connectWithNumber sets state which triggers useEffect in hook
      // The status will change to "loading" then "ready" or "error"
      // We'll reset isConnecting when status changes
    } else {
      console.error("[Chat] connectWithNumber is not available!");
    }
  };

  const handleSendInvite = useCallback(async (data) => {
    if (!sendInvite) return;
    await sendInvite(data);
  }, [sendInvite]);

  return (
    <div className={`${embedded ? "mt-4" : "mt-6"} dark:bg-gray-900`} dir={isRTL ? "rtl" : "ltr"}>
      <div className="flex flex-col">
        <div className="p-4 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-tl-3xl rounded-tr-3xl overflow-hidden shadow-sm dark:shadow-gray-900">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FaComments className="text-[#A4C639] dark:text-[#CBEA7B]" size={20} />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {embedded
                  ? t("pacsShareChatWindow") || "Chat Window"
                  : t("chat")}
              </h2>
              {studyData?.accessionNo && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {t("accNumber")}: {studyData.accessionNo}
                </span>
              )}
              {connectedNumber && (
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  · {t("chatConnectedTo") || "Connected to"}: {connectedNumber}
                </span>
              )}
            </div>
            {hasConversation && (changeNumber || deleteConversation || sendInvite) && (
              <div className="flex items-center gap-2">
                {sendInvite && !hideInviteButton && (
                  <button
                    type="button"
                    onClick={() => setShowInviteModal(true)}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#A4C639] dark:border-[#CBEA7B] bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black hover:opacity-90 transition-colors flex items-center gap-1.5"
                  >
                    <FaEnvelope size={12} />
                    {t("chatSendInvite") || "Send Invite"}
                  </button>
                )}
                {changeNumber && (
                  <button
                    type="button"
                    onClick={handleChangeNumber}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t("chatChangeNumber") || "Change Number"}
                  </button>
                )}
                {deleteConversation && (
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    className="text-xs px-3 py-1.5 rounded-lg border border-red-300 dark:border-red-600 bg-white dark:bg-gray-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? t("loading") || "Deleting..." : t("chatDeleteConversation") || "Delete"}
                  </button>
                )}
              </div>
            )}
          </div>

          {(status === "awaitingNumber" || status === "idle") && !hideNumberEntry && (
            <div className="py-6 space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("chatEnterNumber") || "Enter WhatsApp Number"}
                </label>
                <div className="flex flex-col lg:flex-row gap-2">
                  <input
                    type="text"
                    value={manualNumber}
                    onChange={(e) => {
                      setManualNumber(e.target.value);
                      setNumberError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isConnecting) {
                        handleConnect();
                      }
                    }}
                    placeholder={t("chatNumberPlaceholder") || "+1234567890"}
                    className="w-full lg:flex-1 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B]"
                    disabled={isConnecting}
                  />
                  <button
                    type="button"
                    onClick={handleConnect}
                    disabled={isConnecting || !manualNumber.trim()}
                    className="w-full lg:w-auto lg:min-w-[120px] rounded-lg px-4 py-2 bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isConnecting ? (
                      <>
                        <FaSpinner className="animate-spin" size={14} />
                        <span>{t("loading") || "Connecting..."}</span>
                      </>
                    ) : (
                      <span>{t("chatConnect") || "Connect"}</span>
                    )}
                  </button>
                </div>
                {numberError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{numberError}</p>
                )}
                {errorMessage && status === "awaitingNumber" && (
                  <p className="text-sm text-red-600 dark:text-red-400">{t(errorMessage) || errorMessage}</p>
                )}
              </div>
            </div>
          )}

          {(status === "awaitingNumber" || status === "idle") && hideNumberEntry && (
            <div className="py-4 text-sm text-gray-500 dark:text-gray-400">
              {t("pacsShareConnectNumberHint") ||
                "Connect the WhatsApp number above, then send the template and messages from here."}
            </div>
          )}

          {status === "loading" && (
            <div className="flex items-center justify-center py-12 gap-2 text-gray-600 dark:text-gray-400">
              <FaSpinner className="animate-spin" size={20} />
              <span>{t("loading")}</span>
            </div>
          )}

          {(status === "error" || status === "noCase") && (
            <div className="py-6 px-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
              {t(errorMessage)}
            </div>
          )}

          {status === "ready" && (
            <>
              <div
                ref={messagesContainerRef}
                className={`min-h-[280px] max-h-[420px] overflow-y-auto p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border dark:border-gray-600 mb-4 ${
                  messages.length === 0
                    ? "flex items-center justify-center"
                    : "space-y-3"
                }`}
                style={{ scrollBehavior: "smooth" }}
              >
                {messages.length === 0 ? (
                  <p className="text-center text-gray-500 dark:text-gray-400 text-sm px-4">
                    {t("chatNoMessages")}
                  </p>
                ) : (
                  messages.map((msg) => (
                      <div
                        key={msg.sid}
                        className={`flex ${msg.isFromRadiologist ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                            msg.isFromRadiologist
                              ? "bg-[#CBEA7B] dark:bg-[#A4C63980] text-gray-900 dark:text-gray-100"
                              : "bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 border dark:border-gray-500"
                          }`}
                          dir={isRTL ? "rtl" : "ltr"}
                        >
                          <div className="whitespace-pre-wrap break-words" dir="auto">{msg.body}</div>
                          <div className="text-xs mt-1 opacity-75">
                            {msg.isFromRadiologist ? t("chatYou") : (connectedNumber || "")} ·{" "}
                            {msg.dateCreated
                              ? new Date(msg.dateCreated).toLocaleTimeString(undefined, {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : ""}
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>

              {/* Status Banner */}
              {hasConversation && (
                <div className="mb-3">
                  {!hasTemplateSent ? (
                    <div className="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                      <div className="flex items-start gap-2">
                        <FaExclamationTriangle className="text-yellow-600 dark:text-yellow-400 mt-0.5" size={16} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                            {t("chatSendTemplateFirst") || "Send a template message first"}
                          </p>
                          <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                            {t("chatSendTemplateFirstDesc") || "Free-form messages won't be delivered until you send a template. Click 'Send Invite' to send a template message."}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : !hasPatientResponded ? (
                    <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start gap-2">
                        <FaSpinner className="animate-spin text-blue-600 dark:text-blue-400 mt-0.5" size={16} />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            {t("chatWaitingForResponse") || "Waiting for patient response"}
                          </p>
                          <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                            {t("chatWaitingForResponseDesc") || "Your template has been sent. Once the patient replies, you'll be able to send free-form messages for 24 hours."}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    !is24HourBannerDismissed && (
                      <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 relative flex items-center">
                        <p className="text-xs text-green-700 dark:text-green-300 pr-5 flex-1">
                          {t("chat24HourWindowOpen") || "✓ 24-hour messaging window is open. You can send messages freely."}
                        </p>
                        <button
                          type="button"
                          onClick={() => setIs24HourBannerDismissed(true)}
                          className="flex-shrink-0 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200 opacity-60 hover:opacity-100 transition-opacity flex items-center justify-center"
                          aria-label={t("chatCloseBanner") || "Close"}
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    !hasTemplateSent 
                      ? (t("chatPlaceholderSendTemplate") || "Send a template message first...")
                      : !hasPatientResponded
                      ? (t("chatPlaceholderWaiting") || "Waiting for patient response...")
                      : t("chatPlaceholder")
                  }
                  className={`flex-1 rounded-lg border dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#A4C639] dark:focus:ring-[#CBEA7B] ${
                    !canSendFreeFormMessage ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  disabled={!hasConversation || !canSendFreeFormMessage}
                  dir={isRTL ? "rtl" : "ltr"}
                />
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!hasConversation || !inputText.trim() || !canSendFreeFormMessage}
                  className="rounded-lg px-4 py-2 bg-[#A4C639] dark:bg-[#CBEA7B] text-gray-900 dark:text-black font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  title={!canSendFreeFormMessage ? (t("chatSendDisabledReason") || "Send a template and wait for patient response first") : ""}
                >
                  <FaPaperPlane size={14} />
                  {t("chatSend")}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Invite Template Modal */}
      <InviteTemplateModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSend={handleSendInvite}
        language={language}
        defaultParam1={studyData?.insuranceName || ""}
        defaultParam2={""}
        defaultParam3={defaultParam3 || ""}
        studyId={studyId}
        accessionNo={studyData?.accessionNo}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 flex items-center justify-center z-50"
          onClick={handleDeleteCancel}
        >
          <div
            className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-xl max-w-md w-full mx-4 border dark:border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <FaExclamationTriangle className="text-red-600 dark:text-red-400" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t("chatDeleteConversation") || "Delete Conversation"}
              </h3>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              {t("chatDeleteConfirm") || "Are you sure you want to delete this conversation? This action cannot be undone."}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={handleDeleteCancel}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                {t("cancel") || "Cancel"}
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                className="px-4 py-2 rounded-lg bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 transition-colors font-medium"
              >
                {t("chatDeleteConversation") || "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;
